import React, { useState, useEffect } from 'react';
import { api } from './api';
import Calendar from './Calendar';
import './App.css';

function PublicShell({ children }) {
  return (
    <main className="public-page">
      <nav className="public-nav">
        <a className="public-brand" href="/">CalSync</a>
        <div className="public-links">
          <a href="/privacy">Privacy</a>
          <a href="/tos">Terms</a>
        </div>
      </nav>
      {children}
    </main>
  );
}

function LandingPage() {
  return (
    <PublicShell>
      <section className="public-hero">
        <div className="public-kicker">Private calendar availability sync</div>
        <h1>CalSync keeps your calendars aligned without making your calendar public.</h1>
        <p>
          CalSync is a personal calendar sync app for unifying Zoom Calendar and Google
          Calendar availability. It helps keep work and personal calendars aware of busy
          time across accounts while keeping access restricted to approved users.
        </p>
        <div className="public-actions">
          <button className="login-btn" onClick={api.login}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
              <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
              <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
              <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
            </svg>
            Sign in with Google
          </button>
        </div>
      </section>
      <section className="public-card-grid" aria-label="CalSync features">
        <div className="public-card">
          <h2>Unified calendar view</h2>
          <p>View Zoom and selected Google Calendars together in one private interface.</p>
        </div>
        <div className="public-card">
          <h2>Availability blocking</h2>
          <p>Create busy blocks between connected calendars to reduce double-booking.</p>
        </div>
        <div className="public-card">
          <h2>Restricted access</h2>
          <p>Only configured accounts can sign in. Calendar tokens are stored server-side.</p>
        </div>
      </section>
    </PublicShell>
  );
}

function PrivacyPage() {
  return (
    <PublicShell>
      <article className="public-doc">
        <h1>Privacy Policy</h1>
        <p>Last updated: July 15, 2026</p>
        <p>
          CalSync is a personal calendar synchronization application. It accesses connected
          Google Calendar accounts only to display calendar events, evaluate availability,
          and create, update, or remove calendar blocks requested through the app.
        </p>
        <h2>Data accessed</h2>
        <p>
          CalSync may access calendar event titles, times, calendar identifiers, and event
          metadata needed for synchronization. It does not sell personal data.
        </p>
        <h2>Data storage</h2>
        <p>
          Calendar authorization tokens are stored server-side for background sync. Zoom
          calendar event cache and sync metadata may be stored to operate the service.
        </p>
        <h2>Data sharing</h2>
        <p>
          CalSync does not share calendar data with third parties except service providers
          used to host and operate the application.
        </p>
        <h2>Access control</h2>
        <p>
          Access to the app is restricted to configured accounts. Users can disconnect
          connected calendars from the app settings.
        </p>
        <h2>Contact</h2>
        <p>
          For privacy questions, contact the app owner through the support email shown on
          the Google consent screen.
        </p>
      </article>
    </PublicShell>
  );
}

function TermsPage() {
  return (
    <PublicShell>
      <article className="public-doc">
        <h1>Terms of Service</h1>
        <p>Last updated: July 15, 2026</p>
        <p>
          CalSync is provided as a private calendar sync tool. By using CalSync, you agree
          to use it only with calendar accounts you own or are authorized to connect.
        </p>
        <h2>Permitted use</h2>
        <p>
          The app is intended to help manage personal availability across connected calendar
          accounts. Do not use it to access calendars without permission.
        </p>
        <h2>No warranty</h2>
        <p>
          CalSync is provided as-is. Calendar integrations may depend on third-party APIs
          and services that can change or become unavailable.
        </p>
        <h2>Account access</h2>
        <p>
          Access may be limited to specific approved accounts. The app owner may remove
          access or disconnect integrations at any time.
        </p>
        <h2>Contact</h2>
        <p>
          For questions about these terms, contact the app owner through the support email
          shown on the Google consent screen.
        </p>
      </article>
    </PublicShell>
  );
}

export default function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const path = window.location.pathname;

  useEffect(() => {
    api.me()
      .then(data => {
        if (data.authenticated) setUser(data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="splash">
      <div className="splash-logo">CalSync</div>
      <div className="splash-spinner" />
    </div>
  );

  if (path === '/privacy') return <PrivacyPage />;
  if (path === '/tos') return <TermsPage />;

  if (!user) return <LandingPage />;

  return <Calendar user={user} onLogout={() => { api.logout(); setUser(null); }} />;
}
